import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Calendar, ShieldCheck, Settings, ChevronRight, Wrench, Plus, QrCode, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MODEL_LABEL = { alfa: "Modelo Alfa", omega: "Modelo Ômega", custom: "Personalizado" };
const STATUS_LABEL = { active: "Operacional", in_service: "Em manutenção", retired: "Inactivo", lost: "Perdido / Roubado" };
const STATUS_CLASS = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_service: "bg-amber-100 text-amber-700 border-amber-200",
    retired: "bg-zinc-100 text-zinc-600 border-zinc-200",
    lost: "bg-red-100 text-red-700 border-red-200",
};

export default function ContaEquipamentos() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/account/equipments")
            .then(({ data }) => setItems(data))
            .catch((err) => {
                toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar equipamentos.");
                setItems([]);
            });
    }, []);

    return (
        <div data-testid="conta-equipamentos">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-heading text-2xl font-extrabold text-primary">Os meus equipamentos</h2>
                    <p className="text-sm text-muted-foreground">Carrinhos adquiridos, garantia e estado de cada unidade.</p>
                </div>
                <Link to="/conta/assistencia" data-testid="equip-cta-assistencia"
                    className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-xs font-bold hover:-translate-y-0.5 transition">
                    <Wrench className="h-3.5 w-3.5" /> Pedir assistência
                </Link>
            </div>

            {items === null && (
                <div className="mt-8 text-sm text-muted-foreground">A carregar...</div>
            )}

            {items && items.length === 0 && (
                <div className="mt-8 rounded-2xl border-2 border-dashed border-border bg-white p-10 text-center" data-testid="equip-empty">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary/40 text-primary">
                        <Package className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-extrabold text-primary">Ainda não tem equipamentos registados</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                        Após a entrega, a nossa equipa regista o seu carrinho aqui — com nº de série, garantia e configuração.
                    </p>
                    <Link to="/simulador" className="mt-6 inline-flex items-center gap-2 rounded-md bg-secondary px-5 py-3 text-sm font-bold text-primary hover:-translate-y-0.5 transition">
                        <Plus className="h-4 w-4" /> Configurar um novo carrinho
                    </Link>
                </div>
            )}

            {items && items.length > 0 && (
                <div className="mt-8 grid gap-4 lg:grid-cols-2">
                    {items.map((eq) => {
                        const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";
                        const qrThumb = `${API}/carts/${encodeURIComponent(eq.serial_number)}/qr.png?size=6&public_base=${encodeURIComponent(publicOrigin)}`;
                        return (
                        <article key={eq.id} className="rounded-2xl border border-border bg-white p-6 hover:border-primary/30 hover:shadow-md transition" data-testid={`equip-card-${eq.id}`}>
                            <header className="flex items-start justify-between gap-3">
                                <div>
                                    <span className="eyebrow text-primary/60">{MODEL_LABEL[eq.model] || eq.model}</span>
                                    <h3 className="mt-2 font-heading text-xl font-extrabold text-primary">{eq.nickname || `Carrinho ${eq.serial_number}`}</h3>
                                </div>
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[eq.status] || STATUS_CLASS.active}`}>
                                    {STATUS_LABEL[eq.status] || eq.status}
                                </span>
                            </header>

                            {eq.reported_lost && (
                                <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 flex items-center gap-2 text-xs text-red-800 font-bold">
                                    <AlertTriangle className="h-3.5 w-3.5" /> Reportado como perdido/roubado
                                </div>
                            )}

                            <div className="mt-5 flex gap-4">
                                {eq.serial_number && (
                                    <Link
                                        to={`/conta/carrinhos/${encodeURIComponent(eq.serial_number)}`}
                                        data-testid={`equip-qr-thumb-${eq.id}`}
                                        title="Ver passaporte digital"
                                        className="shrink-0 h-24 w-24 rounded-lg border border-border bg-white p-1.5 hover:border-primary transition group"
                                    >
                                        <img src={qrThumb} alt={`QR ${eq.serial_number}`} className="w-full h-full object-contain group-hover:opacity-90" />
                                    </Link>
                                )}
                                <dl className="flex-1 grid gap-2 text-sm">
                                    <Row icon={Settings} label="Nº série" value={eq.serial_number} />
                                    <Row icon={Calendar} label="Data compra" value={eq.purchase_date || "—"} />
                                    <Row icon={ShieldCheck} label="Garantia até" value={eq.warranty_until || "—"} />
                                    {eq.next_maintenance_at && (
                                        <Row icon={Wrench} label="Próxima manut." value={eq.next_maintenance_at} />
                                    )}
                                </dl>
                            </div>

                            {eq.notes && (
                                <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">{eq.notes}</p>
                            )}
                            <div className="mt-5 flex items-center justify-between flex-wrap gap-2">
                                <Link to={`/conta/carrinhos/${encodeURIComponent(eq.serial_number)}`}
                                    data-testid={`equip-open-passport-${eq.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-bold hover:-translate-y-0.5 transition">
                                    <QrCode className="h-3.5 w-3.5" /> Passaporte digital
                                </Link>
                                <Link to="/conta/assistencia" state={{ equipmentId: eq.id }}
                                    className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:gap-2 transition-all">
                                    Pedir assistência <ChevronRight className="h-3 w-3" />
                                </Link>
                            </div>
                        </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-primary/60" />
            <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground w-24">{label}</span>
            <span className="text-sm font-medium text-primary">{value}</span>
        </div>
    );
}
